import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useRequestCounts } from "../context/RequestCountContext";

interface UseRequestRefreshOptions {
  onRefresh: () => void | Promise<void>;
  requestType: "general" | "dining" | "reservation";
}

export const useRequestRefresh = ({
  onRefresh,
  requestType,
}: UseRequestRefreshOptions) => {
  const location = useLocation();
  const { resetCount } = useRequestCounts();
  const lastPathnameRef = useRef<string>("");

  // Use refs to access the latest functions without causing re-renders
  const onRefreshRef = useRef(onRefresh);
  const locationRef = useRef(location.pathname);
  const resetCountRef = useRef(resetCount);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    resetCountRef.current = resetCount;
  }, [resetCount]);

  // Reset count and refresh data when user visits the page
  useEffect(() => {
    const targetPath = `/${
      requestType === "general"
        ? "general"
        : requestType === "dining"
        ? "in-room-dining"
        : "reservations"
    }`;

    if (
      location.pathname === targetPath &&
      lastPathnameRef.current !== targetPath
    ) {
      lastPathnameRef.current = targetPath;
      resetCount(requestType);
      // Refetch data to show the latest requests
      onRefreshRef.current();
    }
  }, [location.pathname, requestType, resetCount]);

  // Listen for notification events to auto-refresh when user is already on the page
  useEffect(() => {
    const handleNotification = (event: CustomEvent) => {
      const { notificationType, data } = event.detail;

      if (notificationType === "new_guest_request_web" && data?.requesttype) {
        const notificationRequestType = data.requesttype.toLowerCase();

        // Only refresh if it matches our request type AND user is on the page
        if (notificationRequestType === requestType) {
          // Check if user is currently on this page (using ref for current location)
          const targetPath = `/${
            requestType === "general"
              ? "general"
              : requestType === "dining"
              ? "in-room-dining"
              : "reservations"
          }`;

          // Allow sub-paths (e.g., with optional trailing slash or nested segments)
          if (
            locationRef.current === targetPath ||
            locationRef.current.startsWith(`${targetPath}/`)
          ) {
            // Slight delay to allow backend to persist new request before refetch
            setTimeout(() => {
              resetCountRef.current(requestType);
              onRefreshRef.current();
            }, 300);
          }
        }
      }
    };

    // Listen for custom notification events
    window.addEventListener(
      "requestNotification",
      handleNotification as EventListener
    );

    return () => {
      window.removeEventListener(
        "requestNotification",
        handleNotification as EventListener
      );
    };
  }, [requestType]);
};
